import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { LiveTransactionsTab } from './transactions/LiveTransactionsTab';
import { QboHistoryTab } from './transactions/QboHistoryTab';

/**
 * Top-level Transactions workspace.
 *
 *   • Live — the post-cutover (>= 2026-05-01) Mercury / Plaid feed. Editable
 *     QB category, expense approval workflow, Post to QBO. This is the
 *     daily-driver surface.
 *   • History — read-only mirror of QuickBooks' bank-register ledger for the
 *     pre-cutover period. Sourced from the qbo-history-sync Edge Function.
 *
 * The active tab is mirrored to `?tab=...` so links can deep-link a specific
 * view. We default to Live because that's where the operator spends 99% of
 * their time.
 */
const TABS = [
  { id: 'live', label: 'Live' },
  { id: 'history', label: 'History' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TAB_IDS = TABS.map((t) => t.id);

export function TransactionsPage() {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') as TabId | null;
  const active: TabId = tabParam && TAB_IDS.includes(tabParam) ? tabParam : 'live';

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Financials"
        title="Transactions"
      />

      <Tabs
        value={active}
        onValueChange={(v) => {
          const next = new URLSearchParams(params);
          next.set('tab', v);
          setParams(next, { replace: true });
        }}
      >
        <TabsList className="h-auto flex-wrap gap-1 bg-secondary p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:shadow-sm">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="live" className="mt-6">
          <LiveTransactionsTab />
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          <QboHistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
